import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useI18n } from '../../../i18n'
import { useAuth } from '../../AuthProvider'
import { Button } from '../../ui/Button'
import { useNavigate } from 'react-router-dom'
import {
  fetchControlCentricOverview,
  finishControlCentricSession,
  type ControlCentricCompletionSummary,
  type ControlCentricOverview,
} from '../../../services/controlCentricWorkflowApi'
import { saveAssessmentProgress } from '../../../services/complianceLifecycleApi'
import type { RemediationFilterTab } from '../../../pages/compliance-center/journeyRemediationNav'
import { ControlExecutionHeader } from './ControlExecutionHeader'
import { ControlExecutionWorkspace } from './ControlExecutionWorkspace'
import { ControlCentricSequentialWorkbench } from './ControlCentricSequentialWorkbench'
import { ControlCentricControlList } from './ControlCentricControlList'
import { ControlCentricCompletionSummary } from './ControlCentricCompletionSummary'
import { ControlCentricNavigationModeBar } from './ControlCentricNavigationModeBar'
import listStyles from '../../../styles/pages/governance/GovernanceListing.module.scss'

type Lang = 'en' | 'ru' | 'es'

type Props = {
  sessionId: string
  standardCode: string
  lang: Lang
  domainKey: string
  navigationMode: 'SEQUENTIAL' | 'FREE'
  govEvidence?: Array<{ id: string; title?: string; freshnessStatus?: string }>
  onSaveQuestionAnswer: (params: {
    domainKey: string
    questionKey: string
    canonicalControlCode: string | null
    answerValue: string
    assessorNote: string
  }) => Promise<void>
  onOverviewChange?: (overview: ControlCentricOverview | null) => void
  onCompletedChange?: (completed: boolean) => void
  onSwitchSession?: () => void
  onNavigationModeChange?: (mode: 'SEQUENTIAL' | 'FREE') => void
  formatDate: (iso?: string | null) => string
  initialControlKey?: string | null
  remediationMode?: boolean
  remediationFilter?: RemediationFilterTab
}

export function ControlCentricWorkflowWorkspace({
  sessionId,
  standardCode,
  lang,
  domainKey,
  navigationMode,
  govEvidence,
  onSaveQuestionAnswer,
  onOverviewChange,
  onCompletedChange,
  onSwitchSession,
  onNavigationModeChange,
  formatDate,
  initialControlKey = null,
  remediationMode = false,
  remediationFilter = 'ALL',
}: Props) {
  const { token } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [overview, setOverview] = useState<ControlCentricOverview | null>(null)
  const [selectedControl, setSelectedControl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [completionSummary, setCompletionSummary] = useState<ControlCentricCompletionSummary | null>(null)
  const [finishError, setFinishError] = useState<string | null>(null)
  const onOverviewChangeRef = useRef(onOverviewChange)
  const onCompletedChangeRef = useRef(onCompletedChange)
  const completionSummaryRef = useRef(completionSummary)
  onOverviewChangeRef.current = onOverviewChange
  onCompletedChangeRef.current = onCompletedChange
  completionSummaryRef.current = completionSummary

  const loadOverview = useCallback(
    async (silent = false) => {
      if (!token || !sessionId) return
      if (!silent) setLoading(true)
      try {
        const data = await fetchControlCentricOverview(token, sessionId, standardCode, lang)
        setOverview(data)
        onOverviewChangeRef.current?.(data)
        if (data.session?.status === 'COMPLETED') {
          onCompletedChangeRef.current?.(true)
        }
        if (data.session?.status === 'COMPLETED' && !completionSummaryRef.current) {
          setCompletionSummary({
            title: `${standardCode} assessment completed`,
            controlsCompleted: data.progress?.controlsCompleted ?? 0,
            controlsTotal: data.progress?.controlsTotal ?? 25,
            controlsEvidenceSupported: data.progress?.controlsEvidenceSupported ?? 0,
            controlsWithGaps: data.progress?.controlsWithGaps ?? 0,
            partialControls: data.progress?.partialControls ?? 0,
            nonCompliantControls: data.progress?.nonCompliantControls ?? 0,
            evidenceRepositorySummary: data.progress?.evidenceRepositorySummary,
            subtitle: data.session?.stageSubtitle ?? null,
            nextActions: [],
          })
        }
        setSelectedControl((prev) => {
          if (initialControlKey) return initialControlKey
          if (prev) return prev
          return data.controls?.[0]?.controlKey ?? null
        })
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [token, sessionId, standardCode, lang, initialControlKey],
  )

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  const handleSaveAndMaybeFinish = useCallback(
    async (
      params: {
        domainKey: string
        questionKey: string
        canonicalControlCode: string | null
        answerValue: string
        assessorNote: string
      },
      options?: { finishAfterSave?: boolean },
    ) => {
      await onSaveQuestionAnswer(params)
      await loadOverview(true)
      if (options?.finishAfterSave && token) {
        setFinishError(null)
        try {
          const result = await finishControlCentricSession(token, sessionId)
          setCompletionSummary(result.completionSummary)
          onCompletedChange?.(true)
          await loadOverview(true)
        } catch (e: unknown) {
          setFinishError(e instanceof Error ? e.message : t('controlCentric.completion.finishFailed', 'Failed to finish session'))
          throw e
        }
      }
    },
    [onSaveQuestionAnswer, loadOverview, token, sessionId, t],
  )

  const handleStepChange = useCallback((step: { controlKey?: string } | null) => {
    if (step?.controlKey) setSelectedControl(step.controlKey)
  }, [])

  const handleSaveAndExit = useCallback(async () => {
    if (!token || !sessionId) return
    const row = overview?.controls?.find((c) => c.controlKey === selectedControl)
    await saveAssessmentProgress(token, sessionId, {
      mode: remediationMode ? 'REMEDIATION' : 'ASSESSMENT',
      currentControlKey: selectedControl || undefined,
      currentQuestionId: row?.primaryQuestionKey || undefined,
      domainKey: standardCode,
    })
    navigate('/compliance-center/operations')
  }, [token, sessionId, overview?.controls, selectedControl, remediationMode, standardCode, navigate])

  if (loading && !overview) {
    return <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
  }

  if (!overview?.enabled) return null

  if (completionSummary) {
    return (
      <div className="space-y-4">
        <ControlCentricCompletionSummary
          summary={completionSummary}
          standardCode={standardCode}
          sessionId={sessionId}
          onSwitchSession={onSwitchSession}
        />
      </div>
    )
  }

  const isSequential = navigationMode === 'SEQUENTIAL'
  const progressLabel =
    overview.progress?.progressLabel ||
    `${overview.progress?.controlsCompleted ?? 0}/${overview.progress?.controlsTotal ?? 25} controls completed`

  return (
    <div className="space-y-4">
      <ControlExecutionHeader
        standardCode={standardCode}
        stage={overview.session?.stage || t('controlCentric.stage.evidence', 'Evidence Collection')}
        progressLabel={progressLabel}
        lastActivity={overview.session?.updatedAt ? formatDate(overview.session.updatedAt) : null}
        onContinue={() => {
          document.getElementById('cc-control-centric-workspace')?.scrollIntoView({ behavior: 'smooth' })
        }}
      />
      {overview.session?.stageSubtitle ? (
        <p className="text-sm text-amber-800">{overview.session.stageSubtitle}</p>
      ) : null}
      {finishError ? <p className="text-sm text-red-600">{finishError}</p> : null}

      {onNavigationModeChange ? (
        <ControlCentricNavigationModeBar navigationMode={navigationMode} onNavigationModeChange={onNavigationModeChange} />
      ) : null}

      <div className={listStyles.actionsGap}>
        <Button type="button" size="small" color="outline" onClick={() => void handleSaveAndExit()}>
          {t('controlCentric.saveAndExit', 'Save and exit')}
        </Button>
      </div>

      {isSequential ? (
        <div id="cc-control-centric-workspace" className="space-y-4">
          <ControlCentricSequentialWorkbench
            sessionId={sessionId}
            standardCode={standardCode}
            lang={lang}
            controls={overview.controls || []}
            govEvidence={govEvidence}
            initialControlKey={initialControlKey || selectedControl}
            remediationMode={remediationMode}
            remediationFilter={remediationFilter}
            onSaveQuestionAnswer={(p, opts) => handleSaveAndMaybeFinish(p, opts)}
            onStepChange={handleStepChange}
          />
        </div>
      ) : (
        <div id="cc-control-centric-workspace" className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <ControlCentricControlList
              controls={overview.controls || []}
              selectedKey={selectedControl}
              onSelect={setSelectedControl}
              remediationMode={remediationMode}
              remediationFilter={remediationFilter}
            />
          </aside>
          <main className="lg:col-span-8">
            {selectedControl && sessionId ? (
              <ControlExecutionWorkspace
                key={selectedControl}
                sessionId={sessionId}
                controlKey={selectedControl}
                standardCode={standardCode}
                domainKey={domainKey}
                lang={lang}
                govEvidence={govEvidence}
                onRefresh={() => void loadOverview(true)}
                onSaveQuestionAnswer={(p) => handleSaveAndMaybeFinish(p)}
              />
            ) : (
              <p className="text-sm text-slate-600">{t('controlCentric.pickControl', 'Select a control.')}</p>
            )}
          </main>
        </div>
      )}
    </div>
  )
}
